import React, { useState } from 'react'
import styled from 'styled-components'
import { IconButton, Toolbar, DialogContent, Dialog, Tooltip } from '@mui/material'
import { Check, ChevronLeft, ChevronRight, KeyboardArrowDown, Timeline } from '@mui/icons-material'
import theme from 'styles/theme'
import useStartWorkoutDialog from 'activity/hooks/useStartWorkoutDialog'
import {
  momentumLogo,
  restartTimerIcon,
  resumeTimerIcon,
  startTimerIcon,
  stopTimerIcon,
  repsIcon,
  setsIcon,
  weightIcon,
  runningIcon,
  groupInfoDarkIcon,
  flagIcon,
} from 'constants/assets.js'
import parseVideoUrl from 'services/parseVideoUrl'
import formatTime from 'services/formatTime'
import DailyCache from 'common/utils/DailyCache'

import Exercises from './Exercises'
import EndWorkoutDialog from './EndWorkoutDialog'
import WorkoutAnalytics from './WorkoutAnalytics'

export default function StartWorkoutDialog() {
  const {
    state,
    closeStartWorkoutDialog,
    startWorkoutDialog,
    data,
    timerStarted,
    hours,
    minutes,
    seconds,
    startTimer,
    start,
    pause,
    reset,
    isRunning,
    exerciseNumber,
    groupName,
    blockName,
    goToPreviousExercise,
    closeDialog,
    skipCurrentExercise,
    currentExercise,
    markCurrentExerciseAsDone
  } = useStartWorkoutDialog()

  const [workoutAnalytics, setWorkoutAnalytics] = useState(false)
  const [openTooltip, setOpenTooltip] = useState(false)

  const videoUrl = data?.exercises?.[exerciseNumber]?.embed

  const cachedSessionActivities = new DailyCache('sessionActivities')

  let currentExerciseStatus =
    state?.programDialog?.exerciseStatuses?.[currentExercise?.id] || false

  if (currentExercise){
    if (Object.keys(cachedSessionActivities.cache).includes(currentExercise?.id?.toString())) {
      currentExerciseStatus = { done: true }
    }
  }

  const handleToggle = () => {
    setWorkoutAnalytics(true)
  }

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  }

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  }

  return (
    <Dialog.Stylesheet
      fullScreen
      open={startWorkoutDialog.open}
      onClose={closeStartWorkoutDialog}
      disableEnforceFocus
    >
      <Toolbar className="toolbar" style={{marginTop: 40}}>
        <IconButton
          edge="start"
          color="inherit"
          onClick={closeDialog}
        >
          <ChevronLeft sx={{ color: theme.palette.secondary.g800 }} />
        </IconButton>
        <StartWorkoutDialog.Title>{data?.programName}</StartWorkoutDialog.Title>
      </Toolbar>

      <StartWorkoutDialog.Content>
        <div className="container">
          {videoUrl ? (
            <div className="video">
              <iframe
                src={parseVideoUrl(videoUrl)}
                title="Exercise Video"
                allowFullscreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              />
              <button
                  type="button"
                  className="analytics"
                  onClick={handleToggle}
                >
                <Timeline />
              </button>
            </div>
          ) : (
            <div className="noVideo">
              <img src={momentumLogo} alt="Momentum Logo" />
              <button
                  type="button"
                  className="analytics"
                  onClick={handleToggle}
                >
                <Timeline />
              </button>
            </div>
          )}

          <div className="header">
            <div className="currentExercise">
              <p>{data?.programName}</p>
              <div className="currentExerciseName">
                <h3>{currentExercise?.name}</h3>
                <Tooltip
                  title={currentExercise?.exerciseNotes}
                  onClose={handleTooltipClose}
                  open={openTooltip}
                  disableHoverListener
                  >
                    <button className="info" type="button" onClick={handleTooltipOpen}>
                      {currentExercise?.exerciseNotes?.length > 0 &&
                      (<img src={groupInfoDarkIcon} alt="Info Icon" />)}
                    </button>
                </Tooltip>
                {currentExerciseStatus?.done ? (
                  <button type="button" className="done" onClick={markCurrentExerciseAsDone}>
                    <Check />
                    <span>Done</span>
                  </button>
                ) : (
                  <button type="button" style={{background: "#09555D"}} onClick={markCurrentExerciseAsDone}>
                    Mark as done
                  </button>
                )}
              </div>
            </div>
            <div className="exerciseNumber">
              <p>Exercise</p>
              <h3>{`${exerciseNumber + 1}/${data?.exercises?.length}`}</h3>
            </div>
          </div>

          <div className="exerciseStats">
            <div className="setsAndReps">
              <div className="sets">
                <img src={setsIcon} alt="Sets Icon" />
                <span>{currentExercise?.set} Sets</span>
              </div>
              {!!currentExercise?.weight &&
                <div className="weight">
                  <img src={weightIcon} alt="Weight Icon" />
                  <span>{currentExercise?.weight} lbs</span>
                </div>
              }
              {!!Number(currentExercise?.distance) &&
                <div className="distance">
                  <img src={runningIcon} alt="Distance Icon" />
                  <span>{Number(currentExercise?.distance)} m</span>
                </div>
              }
              <div className="reps">
                <img src={repsIcon} alt="Reps Icon" />
                <span>{currentExercise?.reps} Reps</span>
              </div>
              {!!currentExercise?.kpi &&
                <div className="distance">
                  <img src={flagIcon} alt="Distance Icon" />
                  <span>KPI</span>
                </div>
              }
            </div>
          </div>

          <div className="timerAndSkip">
            <div className="timerBarContainer">
              {timerStarted ? (
                <div className="timerBar">
                  <span className="time">
                    {formatTime(hours, minutes, seconds)}
                  </span>
                  <button type="button" onClick={isRunning ? pause : start}>
                    <img
                      src={isRunning ? stopTimerIcon : resumeTimerIcon}
                      alt="Stop/Resume Timer Icon"
                    />
                  </button>
                  <button type="button" onClick={reset}>
                    <img src={restartTimerIcon} alt="Restart Timer Icon" />
                  </button>
                </div>
              ) : (
                <button className="timerBar" type="button" onClick={startTimer}>
                  <img src={startTimerIcon} alt="Start Timer Icon" />
                  <span className="label">Start Timer</span>
                </button>
              )}
            </div>

            <div className="buttonContainer">
              <button type="button" onClick={goToPreviousExercise}>
                <ChevronLeft sx={{ color: '#fff' }} />
                <span>Back</span>
              </button>
              {data?.exercises?.length > exerciseNumber + 1 && (
                <button
                  type="button"
                  className="nextTrainingBtn"
                  onClick={skipCurrentExercise}
                >
                  <span>Next</span>
                  <ChevronRight sx={{ color: '#fff' }} />
                </button>
              )}
            </div>

            {/* {data?.exercises?.length > exerciseNumber + 1 && ( */}
            {/*   <div className="buttonContainer"> */}
            {/*     <button */}
            {/*       type="button" */}
            {/*       className="nextTrainingBtn" */}
            {/*       onClick={skipCurrentExercise} */}
            {/*     > */}
            {/*       <img src={runnerIcon} alt="Runner Icon" /> */}
            {/*       <span>Next</span> */}
            {/*     </button> */}
            {/*   </div> */}
            {/* )} */}
          </div>
        </div>

        <div className="exercisesControl">
          {data?.exercises?.length > exerciseNumber + 1 && (
            <div className="upNext">
              <div className="label">
                <p>Up Next</p>
                <KeyboardArrowDown sx={{ color: '#1B1B1B' }} />
              </div>

              <Exercises
                workoutMode
                currentExerciseNumber={exerciseNumber}
                currentGroupName={groupName}
                currentBlockName={blockName}
              />
            </div>
          )}
        </div>

        <EndWorkoutDialog />
        <WorkoutAnalytics open={workoutAnalytics} onOpenChange={setWorkoutAnalytics}/>
      </StartWorkoutDialog.Content>
    </Dialog.Stylesheet>
  )
}

Dialog.Stylesheet = styled(Dialog)`
  padding-bottom: env(safe-area-inset-bottom);

  .toolbar {
    text-align: center;
    h3 {
      margin: 0;
      margin-right: 2rem;
      font-weight: 700;
    }
  }

  .btnContainer {
    text-align: center;
    margin-top: 2rem;
    padding: 1rem;
    .startNowBtn {
      border: 0;
      border-radius: 0.625rem;
      width: 100%;
      padding: 1rem;
      text-transform: uppercase;
      color: #fff;
      font-weight: 700;
      font-size: 0.875rem;
      background-color: #09555d;
      letter-spacing: 0.05rem;
    }
  }
`

StartWorkoutDialog.Title = styled.h3`
  color: ${theme.palette.secondary.g800};
  flex: 1;
  margin-bottom: 0;
`
StartWorkoutDialog.Content = styled(DialogContent)`
  padding: 0 !important;

  .video {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    display: flex;
    justify-content: flex-end;

    .analytics {
      z-index: 100;
      position: relative;
      border: none;
      background-color: ${theme.palette.secondary.g800};
      border-radius: 0.625rem;
      margin: 0 1rem 0 1rem;
      color: #fff;
      width: 50px;
      height: 50px;
      margin-right: 5vw;
      margin-top: 35vh;

      @media screen and (max-height: 1200px) and (min-width: 700px){
        margin-right: 5vw;
      }

      @media screen and (max-height: 1200px) and (min-width: 700px) and (max-width: 1000px){
        margin-right: 5vw;
        margin-top: 10vh;
      }

      @media screen and (max-width: 700px) {
        margin-right: 5vw;
      }
    }

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
      padding: 0 1rem 0 1rem;

      @media screen and (max-width: 765px) {
        border-radius: 0;
        padding: 0;
      }
    }

    @media screen and (max-width: 765px) {
      height: 29rem;
      border-radius: 0;
    }
  }

  .noVideo {
    position: relative;
    width: 100%;
    max-width: 30rem;
    padding: 0 1rem 0 1rem;
    margin: 0 auto;
    display: flex;
    justify-content: flex-end;
    align-items: end;

    img {
      padding: 5rem;
      width: 100%;
    }

    .analytics {
      z-index: 100;
      position: absolute;
      border: none;
      background-color: ${theme.palette.secondary.g800};
      border-radius: 0.625rem;
      margin: 0 1rem 0 1rem;
      color: #fff;
      width: 50px;
      height: 50px;
      margin-bottom: 5vh;
    }
  }

  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media screen and (max-width: 765px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .container {
    padding: 0 !important;
    max-width: none;

    .header {
      margin-top: 2rem;
      padding: 0 1rem 0 1rem !important;
      display: flex;
      justify-content: space-between;
      font-weight: 500;
      color: #3a4750;
      p {
        margin: 0;
      }
      .currentExercise {
        h3 {
          font-weight: 600;
          font-size: 1.2rem;
          margin: 0;
          margin-right: 0.5rem;
        }

        .currentExerciseName {
          display: flex;
          align-items: center;

          h3 {
            max-width: 11rem;
          }

          img {
            cursor: pointer;
          }

          button {
            width: 7rem;
            margin-left: 1rem;
            border: 0;
            border-radius: 1.875rem;
            background-color ${theme.palette.secondary.g800};
            color: #fff;
            padding: 0 0.5rem 0 0.5rem;
            font-weight: 600;
            font-size: 0.75rem;
            height: 2rem;

            @media screen and (max-width: 390px) {
              width: 12rem;
            }
          }

          .done {
            margin-left: 1rem;
            background-color: #198F96;
            border-radius: 1.875rem;
            color: #fff;
            padding: 0 0.5rem 0 0.5rem;
            display: flex;
            align-items: center;
            font-weight: 600;
            font-size: 0.75rem;
            height: 2rem;
            width: 5rem;

            span {
              padding: 0 0.5rem 0 0.5rem;
            }
          }
          .info{
            border: 0;
            background-color: #fff;
            width: 2.5rem;
            margin-left: 0px;
            margin-bottom: 0.3rem;
          }
        }
      }
      .exerciseNumber {
        text-align: center;
        h3 {
          font-weight: 700;
          font-size: 1.5rem;
          font-family: 'Open Sans', sans-serif;
          color: #3a4750;
        }
      }
    }

    .exerciseStats {
      margin-left: 10px;

      .setsAndReps {
        display: flex;
        .sets {
          padding: 10px;
        }
        .reps {
          padding: 10px;
        }
        .weight {
          padding: 10px;
        }
        .distance {
          padding: 10px;
        }
      }
    }

    .timerAndSkip {
      padding: 0 1rem 0 1rem !important;
      display: flex;
      margin-bottom: 2rem;
      .timerBarContainer {
        .timerBar {
          margin-right: 1rem;
          border: 0;
          padding: 1rem;
          border-radius: 0.625rem;
          color: ${theme.palette.secondary.g800};
          display: flex;
          align-items: center;
          justify-content: center;
          width: 14rem;
          font-weight: 600;
          background-color: #f8f8f8;

          button {
            border: 0;
            margin: 0;
            padding: 0;
            background-color: #f8f8f8;
          }

          span {
            height: 100%;
            padding: 0.3rem 0 0.3rem 0;
          }

          .label {
            margin-left: 0.5rem;
          }

          .time {
            font-weight: 700;
            font-size: 1.75rem;
            margin-right: 1.5rem;
            width: 6ch;
            font-family: 'Open Sans', sans-serif;
          }

          img {
            margin-right: 0.5rem;
          }

          @media screen and (max-width: 810px) {
            padding-right: 0.2rem;
            padding-left: 0.2rem;
            width: 100%;
            .time {
              font-size: 1rem;
            }
          }
        }

        @media screen and (max-width: 810px) {
          width: 100%;
          margin-right: 0.5rem;
        }
      }

      .buttonContainer {
        width: 100%;
        display: flex;
        align-items: center;
        margin-left: 0.5rem;

        button {
          border: none;
          background-color: ${theme.palette.secondary.g800};
          border-radius: 0.625rem;
          margin: 0 1rem 0 1rem;
          color: #fff;
          width: 100%;
          height: 100%;
          .nextTrainingBtn {
            height: 100%;
            border: 0;
            background-color: ${theme.palette.secondary.g800};
            color: #fff;
            border-radius: 0.625rem;
            padding: 1rem;
            font-weight: 700;
            width: 100%;

            span {
              margin-left: 0.5rem;
              text-transform: uppercase;
            }
          }

          @media screen and (max-width: 700px) {
            margin-left: 0;
            margin-right: 0.2rem;
          }
        }
      }
    }
  }

  .upNext {
    text-align: center;

    .label {
      display: flex;
      justify-content: center;
      margin-bottom: -2rem;
    }

    p {
      font-weight: 600;
      color: #191919;
    }
  }

  .getReady {
    text-align: center;
    padding-top: 14rem;
    h2 {
      color: ${theme.palette.secondary.g800};
      font-size: 2rem;
    }

    .timer {
      div {
        text-align: center;
        margin: 0 auto;
      }
      p {
        font-size: 6.5625rem;
        font-weight: 600;
        color: ${theme.palette.secondary.g800};
      }
    }
  }
`
